import type { UserInfo } from '/#/store';
import type { ErrorMessageMode } from '/#/axios';
import { defineStore } from 'pinia';
import { store } from '@/store';
import { RoleEnum } from '@/enums/roleEnum';
import { ROLES_KEY, TOKEN_KEY, USER_INFO_KEY } from '@/enums/cacheEnum';
import { getAuthCache, setAuthCache } from '@/utils/auth';
import { GetUserInfoModel, LoginParams } from '@/api/sys/model/userModel';
import { doLogout, getUserInfo, loginApi } from '@/api/sys/user';
import { useI18n } from '@/hooks/web/useI18n';
import { useMessage } from '@/hooks/web/useMessage';
import { usePermissionStore } from '@/store/modules/permission';
import { isArray } from 'xmgoing-utils';
import { h } from 'vue';
import {dbUtils} from 'xmgoing-utils';
import GoingUtils from '@/utils/goingUtils';
import {StorageUtils} from 'xmgoing-utils';
import emitter from "@/events/eventBus";
import {loadOutSystem} from "@/utils/xmCom/loadSystem";

interface UserState {
  showAppFlag?: boolean;
  userInfo: Nullable<UserInfo>;
  token?: string;
  roleList: RoleEnum[];
  sessionTimeout?: boolean;
  lastUpdateTime: number;
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    showAppFlag: false,
    // user info
    userInfo: null,
    // token
    token: undefined,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    // Last fetch time
    lastUpdateTime: 0,
  }),
  getters: {
    getUserInfo(): UserInfo {
      return this.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY) || {};
    },
    getToken(): string {
      return this.token || getAuthCache<string>(TOKEN_KEY);
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY);
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
  },
  actions: {
    setToken(info: string | undefined) {
      this.token = info ? info : ''; // for null or undefined value
      setAuthCache(TOKEN_KEY, info);
    },
    setShowAppFlag(shoFlag: boolean) {
      this.showAppFlag = shoFlag;
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setUserInfo(info: UserInfo | null) {
      if(info){
        StorageUtils.addSessionItem('curUserId',info.id);
      }
      this.userInfo = info;
      this.lastUpdateTime = new Date().getTime();
      setAuthCache(USER_INFO_KEY, info);
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    resetState() {
      this.userInfo = null;
      this.token = '';
      this.roleList = [];
      this.sessionTimeout = false;
    },
    /**
     * @description: login
     */
    async login(
      params: LoginParams & {
        goHome?: boolean;
        mode?: ErrorMessageMode;
      },
    ): Promise<GetUserInfoModel | null> {
      try {
        const { goHome = true, mode, ...loginParams } = params;
        const data = await loginApi(loginParams, mode);
        const { token } = data;

        // save token
        this.setToken(token);
        return this.afterLoginAction();
      } catch (error) {
        return Promise.reject(error);
      }
    },
    /**
     * 登陆后的操作 获取用户菜单的数据 跳转到默认首页
     */
    async afterLoginAction(backFun?: any): Promise<GetUserInfoModel | null> {
      if (!this.getToken) return null;
      const curJsonStr=StorageUtils.getSessionItem("curUserJson");
      // get user info
      let userInfo: any = null;
      if(curJsonStr&&curJsonStr.length>0){
        try{
          userInfo=JSON.parse(curJsonStr);
        }catch (e){
          userInfo=await this.getUserInfoAction();
        }
      }else{
        userInfo=await this.getUserInfoAction();
      }
      window.appObj.accountId = userInfo['tenantId'];
      this.setUserInfo(userInfo);
      const sessionTimeout = this.sessionTimeout;
      if (sessionTimeout) {
        this.setSessionTimeout(false);
      } else {
        const permissionStore = usePermissionStore();
        const { position }: any = userInfo;
        //如果是单位管理员
        if (GoingUtils.isUnitAdmin(position)) {
          await permissionStore.buildAdminRoutesAction();
        }else  if (GoingUtils.isGroupAdmin(position)) {
          await permissionStore.buildAdminRoutesAction();
        }else  if (GoingUtils.isSystem(position)) {
          StorageUtils.addSessionItem("position",position)
        } else {
          await permissionStore.buildRoutesAction();
        }
      }
      backFun && backFun();
      return userInfo;
    },
    async getUserInfoAction(): Promise<UserInfo | null> {
      const userInfo = await getUserInfo();
      const { roleId } = userInfo;
      const roles = roleId.split(';');
      //设置当前用户的角色信息
      if (isArray(roles)) {
        const roleList = roles.map((item) => item.value) as RoleEnum[];
        this.setRoleList(roleList);
      } else {
        userInfo.roles = [];
        this.setRoleList([]);
      }
      dbUtils.saveCachUser(userInfo);
      this.setUserInfo(userInfo);
      return userInfo;
    },
    /**
     * @description: logout
     */
    async logout(goLogin = false, isClearUser = true) {
      return new Promise(async (resolve, reject) => {
        const userInfo = dbUtils.getCachUser();
        window.localStorage.setItem("curHref","");
        if (userInfo) {
          window.appObj.tenantId = userInfo['tenantId'];
          window.appObj.accountId = userInfo['accountId'];
        }
        if (this.getToken) {
          try {
            await doLogout();
          } catch {
            reject('注销Token失败');
          }
        }
        this.setToken(undefined);
        this.setSessionTimeout(false);
        this.setUserInfo(null);
        if (goLogin) {
          if(emitter&&window.appObj.logoutFlag!==true){
            isClearUser&&dbUtils.clearSessionItems();
            window.appObj.sessionTimeout=true;
            emitter.emit('useLockStore')
          }else{
            loadOutSystem({},isClearUser);
          }
          setTimeout(() => {
            window.location.reload();
          }, 500);
        }
        resolve(true);
      });
    },

    /**
     * @description: Confirm before logging out
     */
    confirmLoginOut() {
      const { createConfirm } = useMessage();
      const { t } = useI18n();
      createConfirm({
        iconType: 'warning',
        title: () => h('span', t('sys.app.logoutTip')),
        content: () => h('span', t('sys.app.logoutMessage')),
        onOk: async () => {
          window.appObj.logoutFlag=true;
          await this.logout(true);
        },
      });
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
